import React, { useEffect, useState } from 'react';
import tw, { styled } from 'twin.macro';

import { Placeholder, Flex, Heading1, Span } from '@ubisend/pulse-components';
import { useAuth } from '@ubisend/pulse-auth';
import {
  useQuery,
  EmbedBody,
  AuthContext,
  BotProvider,
  formatSettings,
  ComposerProvider,
  ConverseComposer,
  ConverseMessages,
  keepPreviousData,
  LanguageProvider,
  DemoDeliveryProvider,
  createInitialMessages,
  SubscriberTypingProvider
} from '@ubisend/pulse-volt';
import { useSetupDemo } from '@ubisend/pulse-demo';
import { useLanguages } from '@ubisend/pulse-hooks';

import { useBank, useLanguage } from '../../hooks/index';

const BASE_URL =
  process.env.NODE_ENV === 'test'
    ? 'https://localhost:3000'
    : process.env.REACT_APP_API_URL.substring(
      0,
      process.env.REACT_APP_API_URL.lastIndexOf('/api')
    );

const StyledEmbedBody = styled(EmbedBody)`
  ${tw`flex flex-col h-full flex-grow`}
`;

const EmbedDemo = () => (
  <>
    <StyledEmbedBody>
      <ConverseMessages />
    </StyledEmbedBody>
    <ComposerProvider>
      <ConverseComposer />
    </ComposerProvider>
  </>
);

const formatQueryData = data => {
  const { bot } = formatSettings(data.settings);

  return {
    language: data.language,
    bot: {
      ...bot,
      initialMessages: createInitialMessages(data.messages)
    }
  };
};

const Demo = () => {
  const { client } = useAuth();

  const { language } = useLanguage();

  const { languages } = useLanguages();

  const { demo_trigger } = useBank();

  const [token, setToken] = useState(null);

  const [statusCode, setStatusCode] = useState();

  const mutation = useSetupDemo({
    onSuccess: ({ data, status }) => {
      setStatusCode(status);
      setToken(data.token);
    }
  });

  const query = useQuery({
    queryKey: [`${BASE_URL}/bot/embed/auth`, { token, demo: true }],
    // Pulse params
    token,
    clientId: client.identifier,
    browserUrl: null,
    variables: [],
    channelDriverName: 'Embedded',
    demo: true,
    // Query params
    enabled: Boolean(token),
    gcTime: 0,
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (query.isSuccess && query.data?.token) {
      setToken(query.data.token);
    }
  }, [query.isSuccess, query.data, setToken]);

  const { language_id, ...rest } = demo_trigger;
  useEffect(() => {
    mutation.mutate({ ...rest, language_id: language });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.mutate, demo_trigger]);

  if (mutation.isError) {
    return (
      <Flex col fat="true" tall center middle pad>
        <Heading1>Something went wrong</Heading1>
        <Span>Please try again later.</Span>
      </Flex>
    );
  }

  if (mutation.isSuccess && statusCode === 204) {
    return (
      <Flex col fat="true" tall center middle pad>
        <Heading1>Demo unavailable</Heading1>
        <Span middle mt style={{ textAlign: 'center' }}>
          Workflows with automated steps are not currently available here.
        </Span>
        <Span mt>
          Please use your{' '}
          <a rel="nofollow noopener noreferrer" target="_blank" href="/demo">
            Demo
          </a>{' '}
          area.
        </Span>
      </Flex>
    );
  }

  if (!query.data || query.isLoading) {
    return (
      <Flex pad>
        <Placeholder items={1} subitems={15} />
      </Flex>
    );
  }

  const { bot } = formatQueryData(query.data);

  return (
    <AuthContext.Provider value={{ token: query.data.token, setToken, query }}>
      <BotProvider {...bot} name="Demo bot">
        <DemoDeliveryProvider
          client_id={client.identifier}
          channelDriverName="Embedded"
          browserUrl={null}
          variables={[]}
          base_url={BASE_URL}>
          <LanguageProvider
            initialLanguage={languages.find(({ id }) => id === language)}
            namespaces={['bots', 'embed']}>
            <SubscriberTypingProvider>
              <EmbedDemo />
            </SubscriberTypingProvider>
          </LanguageProvider>
        </DemoDeliveryProvider>
      </BotProvider>
    </AuthContext.Provider>
  );
};

export default Demo;
